/**
 * Date formatting utility for newsroom bulletins
 * Supports locale-specific formatting with midnight offset rules
 */

export interface DateFormattingConfig {
  enabled: boolean;
  locale: string;
  midnightOffset: boolean;
  customMappings?: {
    days: Record<string, string>;
    months: Record<string, string>;
    dayNumbers: Record<string, string>;
    yearFormats: Record<string, string>;
  };
}

// Default Greek mappings
const GREEK_DAYS: Record<string, string> = {
  Monday: "Δευτέρα",
  Tuesday: "Τρίτη",
  Wednesday: "Τετάρτη",
  Thursday: "Πέμπτη",
  Friday: "Παρασκευή",
  Saturday: "Σάββατο",
  Sunday: "Κυριακή"
};

const GREEK_MONTHS: Record<string, string> = {
  "1": "Ιανουαρίου",
  "2": "Φεβρουαρίου",
  "3": "Μαρτίου",
  "4": "Απριλίου",
  "5": "Μαΐου",
  "6": "Ιουνίου",
  "7": "Ιουλίου",
  "8": "Αυγούστου",
  "9": "Σεπτεμβρίου",
  "10": "Οκτωβρίου",
  "11": "Νοεμβρίου",
  "12": "Δεκεμβρίου"
};

const GREEK_DAY_NUMBERS: Record<string, string> = {
  "1": "πρώτη", "2": "δύο", "3": "τρεις", "4": "τέσσερις", "5": "πέντε",
  "6": "έξι", "7": "επτά", "8": "οκτώ", "9": "εννέα", "10": "δέκα",
  "11": "έντεκα", "12": "δώδεκα", "13": "δεκατρείς", "14": "δεκατέσσερις",
  "15": "δεκαπέντε", "16": "δεκαέξι", "17": "δεκαεπτά", "18": "δεκαοκτώ",
  "19": "δεκαεννέα", "20": "είκοσι", "21": "εικοσιμία", "22": "εικοσιδύο",
  "23": "εικοσιτρείς", "24": "εικοσιτέσσερις", "25": "εικοσιπέντε", "26": "εικοσιέξι",
  "27": "εικοσιεπτά", "28": "εικοσιοκτώ", "29": "εικοσιεννέα", "30": "τριάντα",
  "31": "τριανταμία"
};

const GREEK_YEAR_FORMATS: Record<string, string> = {
  "2026": "δύο χιλιάδες εικοσιέξι",
  "2025": "δύο χιλιάδες εικοσιπέντε",
  "2024": "δύο χιλιάδες εικοσιτέσσερα",
  "2023": "δύο χιλιάδες εικοσιτρία"
};

/**
 * Apply midnight offset rule
 * If time is before midnight (hour 0), use the next day
 */
export function applyMidnightOffset(date: Date): Date {
  const offsetDate = new Date(date);
  const hour = offsetDate.getHours();
  
  if (hour === 0) {
    offsetDate.setDate(offsetDate.getDate() + 1);
  }
  
  return offsetDate;
}

/**
 * Format date for Greek newsroom bulletins
 * Format: "Δευτέρα πρώτη Ιανουαρίου δύο χιλιάδες εικοσιπέντε"
 */
function formatGreekDate(date: Date, customMappings?: DateFormattingConfig['customMappings']): string {
  const days = customMappings?.days || GREEK_DAYS;
  const months = customMappings?.months || GREEK_MONTHS;
  const dayNumbers = customMappings?.dayNumbers || GREEK_DAY_NUMBERS;
  const yearFormats = customMappings?.yearFormats || GREEK_YEAR_FORMATS;
  
  // Get date components
  const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
  const day = date.getDate().toString();
  const month = (date.getMonth() + 1).toString();
  const year = date.getFullYear().toString();
  
  // Translate components
  const greekDay = days[dayName] || dayName;
  const greekMonth = months[month] || "Μη έγκυρος μήνας";
  const greekDayNumber = dayNumbers[day] || `Μη έγκυρη ημέρα (${day})`;
  const greekYear = yearFormats[year] || year;
  
  // Validate
  if (greekMonth.includes("Μη έγκυρος") || greekDayNumber.includes("Μη έγκυρη")) {
    throw new Error(`Invalid date components: day=${day}, month=${month}`);
  }
  
  return `${greekDay} ${greekDayNumber} ${greekMonth} ${greekYear}`;
}

/**
 * Main date formatting function
 * Applies locale-specific formatting with optional midnight offset
 */
export function formatDateForNewsroom(
  date: Date,
  config: DateFormattingConfig
): string {
  if (!config.enabled) {
    return date.toISOString().split('T')[0]; // Return ISO date if disabled
  }
  
  // Apply midnight offset if configured
  let formattingDate = date;
  if (config.midnightOffset) {
    formattingDate = applyMidnightOffset(date);
  }
  
  // Format based on locale
  switch (config.locale) {
    case 'el-GR':
    case 'greek':
      return formatGreekDate(formattingDate, config.customMappings);
    
    case 'en-US':
    case 'english':
      // Example: "Monday, January 1st, 2025"
      return formattingDate.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    
    default:
      // Default to ISO date
      return formattingDate.toISOString().split('T')[0];
  }
}

/**
 * Get default config for Greek newsroom
 */
export function getDefaultGreekConfig(): DateFormattingConfig {
  return {
    enabled: true,
    locale: 'el-GR',
    midnightOffset: true,
    customMappings: {
      days: GREEK_DAYS,
      months: GREEK_MONTHS,
      dayNumbers: GREEK_DAY_NUMBERS,
      yearFormats: GREEK_YEAR_FORMATS
    }
  };
}
